import { isArray } from 'lodash';
import moment from 'moment';
import {
    updateAppointmentDateFormat,
    updateArchiveDateFormat,
    updateAttendedDateFormat,
    updateconsultationDateFormat,
    updateDateFormat,
    updateLastContactDateFormat,
} from 'src/redux/slices/lead';
import { dispatch } from 'src/redux/store';

// Maps backend date_format_config values ('date'|'time'|'both') to the
// Redux/localStorage tokens used by cell renderers.
const BACKEND_TO_REDUX_FORMAT = {
    date: 'date_format',
    time: 'human_readable',
    both: 'date_time_format',
};

/**
 * Seeds the 6 Redux date-format states using the priority chain:
 *   localStorage user override → practice.preferences.date_format_config → 'date_time_format'
 *
 * Call this after practice data loads (e.g. inside getPracticeItem).
 * The handleChangeDateFormat user-override path (localStorage + Redux dispatch) remains
 * unchanged — it continues to take precedence because localStorage is checked first here.
 *
 * TODO: If backend confirms a different field name for date_format_config, update the
 * cfg[key] lookups below.
 */
export const seedDateFormatsFromPractice = (dateFormatConfig) => {
    const cfg = dateFormatConfig || {};
    const resolve = (lsKey, cfgKey) =>
        localStorage.getItem(lsKey) || BACKEND_TO_REDUX_FORMAT[cfg[cfgKey]] || 'date_time_format';

    dispatch(updateDateFormat(resolve('dateFormat', 'created_at')));
    dispatch(updateArchiveDateFormat(resolve('dateArchiveFormat', 'archived_at')));
    dispatch(updateAppointmentDateFormat(resolve('appointmentFormat', 'appointment_datetime')));
    dispatch(updateconsultationDateFormat(resolve('consultationDateFormat', 'consultation_date')));
    dispatch(updateLastContactDateFormat(resolve('lastContactFormat', 'last_contact')));
    dispatch(updateAttendedDateFormat(resolve('attendedFormat', 'date_attended')));
};

export const handleChangeDateFormat = (e, data, setValue) => {
    switch (data) {
        case 'archived_at':
            localStorage.setItem('dateArchiveFormat', e.target.value);
            dispatch(updateArchiveDateFormat(e.target.value));
            setValue('archiveTitle', e.target.value);
            break;
        case 'created_at':
        case 'create_date':
            localStorage.setItem('dateFormat', e.target.value);
            dispatch(updateDateFormat(e.target.value));
            setValue('title', e.target.value);
            break;
        case 'lastContact':
            localStorage.setItem('lastContactFormat', e.target.value);
            dispatch(updateLastContactDateFormat(e.target.value));
            setValue('lastContactTitle', e.target.value);
            break;
        case 'date_attended':
            localStorage.setItem('attendedFormat', e.target.value);
            dispatch(updateAttendedDateFormat(e.target.value));
            setValue('attendedTitle', e.target.value);
            break;
        case 'lead_treatments__appointments__datetime':
            localStorage.setItem('appointmentFormat', e.target.value);
            dispatch(updateAppointmentDateFormat(e.target.value));
            setValue('appointmentsTitle', e.target.value);
            break;
        case 'consultation_date':
            localStorage.setItem('consultationDateFormat', e.target.value);
            dispatch(updateconsultationDateFormat(e.target.value));
            setValue('consultationDateTitle', e.target.value);
            break;
        default:
            break;
    }
};

// The treatment's start date: the timestamp of the log whose transition put it
// into TREATMENT_STARTED. A treatment can hold several — `to_treatment_started`
// accepts CONSULTATION_ATTENDED and CLOSED as sources, so start -> undo -> start
// leaves two — and the current one is the most recently CREATED, i.e. the
// highest log id.
//
// This used to pick `moment.max` over the timestamps, which is wrong for two
// reasons (RCA 2026-09-17, lead 191300 / practice 16):
//
//   1. The backend's update_transition_time wrote a different row, so the date
//      the user picked was saved somewhere this function never looked at. The
//      API returned 200 and the column never moved.
//   2. `timestamp` is the field the Started-date column edits, so it cannot
//      also be what identifies the row. Back-dating the current start below one
//      of its siblings made a sibling "the latest", and this function then
//      started rendering that other row instead. `id` never moves.
//
// Backend counterpart: apps/leads/views.py::update_transition_time orders by
// `-pk` for the same reason. Change one and you must change the other.
export const getTransitionDate = (logs) => {
    const val = logs?.filter((el) => el.transition === 'to_treatment_started');
    if (!isArray(val) || val.length === 0) return '';
    const current = val.reduce((a, b) => ((Number(b?.id) || 0) > (Number(a?.id) || 0) ? b : a));
    return current?.timestamp || '';
};

export const showArchivedDate = (data, row) => {
    const dateTime = row?.archived_at;
    if (!dateTime) return row.archived_at_human ?? '';
    if (data === 'date_format') {
        return moment(dateTime, 'DD-MM-YYYY HH:mm:ss').format('ddd DD MMM YYYY');
    }
    if (data === 'date_time_format') {
        const formattedDate = moment(dateTime, 'DD-MM-YYYY HH:mm:ss').format('ddd DD MMM YYYY');
        const formattedTime = moment(dateTime, 'DD-MM-YYYY HH:mm:ss').format('hh:mm A');
        return `${formattedDate} <span>at ${formattedTime}</span>`;
    }
    return row.archived_at_human ?? '';
};